import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DollarSign, TrendingUp, Clock, Users, Video, Eye, Heart, 
  Wallet, ArrowUpRight, Calendar, Info, Loader2, CheckCircle2,
  BarChart3, Gift, Star, Crown, Sparkles, Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export const CreatorDashboardPage = () => {
  const [earnings, setEarnings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [fundInfo, setFundInfo] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [tier, setTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('');
  const [payoutDetails, setPayoutDetails] = useState('');
  const [requestingPayout, setRequestingPayout] = useState(false);
  const { axiosInstance, user } = useAuth();


  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [earningsRes, analyticsRes, fundRes, payoutsRes, tierRes] = await Promise.all([
        axiosInstance.get('/creator/earnings'),
        axiosInstance.get('/creator/analytics?days=30'),
        axiosInstance.get('/creator/fund-info'),
        axiosInstance.get('/creator/payouts'),
        axiosInstance.get('/creator/tier'),
      ]);
      setEarnings(earningsRes.data);
      setAnalytics(analyticsRes.data);
      setFundInfo(fundRes.data);
      setPayouts(payoutsRes.data);
      setTier(tierRes.data);
    } catch (err) {
      console.error('Failed to fetch creator data:', err);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRequestPayout = async () => {
    if (!payoutAmount || !payoutMethod || !payoutDetails) {
      toast.error('Please fill in all fields');
      return;
    }

    const amount = parseFloat(payoutAmount);
    if (amount < (earnings?.minimum_payout || 10)) {
      toast.error(`Minimum payout is $${earnings?.minimum_payout || 10}`);
      return;
    }

    if (amount > (earnings?.available_balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    setRequestingPayout(true);
    try {
      await axiosInstance.post(
        `/creator/payout/request?amount=${amount}&payout_method=${payoutMethod}&payout_details=${encodeURIComponent(payoutDetails)}`
      );
      toast.success('Payout request submitted!');
      setShowPayoutModal(false);
      setPayoutAmount('');
      setPayoutMethod('');
      setPayoutDetails('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;
  const formatTime = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    return `${hours.toFixed(1)}h`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="pb-32 px-6 max-w-7xl mx-auto">
      <div className="py-8">
        {/* Header with Tier Badge */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">
                Creator Dashboard
              </h1>
              <p className="text-zinc-400">Track your earnings and video performance</p>
            </div>
          </div>
          
          <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:opacity-90 text-white rounded-full px-6"
                disabled={(earnings?.available_balance || 0) < (earnings?.minimum_payout || 10)}
                data-testid="request-payout-btn"
              >
                <Wallet className="w-5 h-5 mr-2" />
                Request Payout
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white">Request Payout</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Available Balance</label>
                  <div className="text-2xl font-bold text-green-400">
                    {formatCurrency(earnings?.available_balance)}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Amount to Withdraw</label>
                  <Input
                    type="number"
                    placeholder={`Min $${earnings?.minimum_payout || 10}`}
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="payout-amount-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Payout Method</label>
                  <Select value={payoutMethod} onValueChange={setPayoutMethod}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="paypal" className="text-white">PayPal</SelectItem>
                      <SelectItem value="bank_transfer" className="text-white">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">
                    {payoutMethod === 'paypal' ? 'PayPal Email' : 'Bank Account Details'}
                  </label>
                  <Input
                    placeholder={payoutMethod === 'paypal' ? 'your@email.com' : 'Account number & routing'}
                    value={payoutDetails}
                    onChange={(e) => setPayoutDetails(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="payout-details-input"
                  />
                </div>
                
                <Button
                  onClick={handleRequestPayout}
                  disabled={requestingPayout}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full"
                  data-testid="confirm-payout-btn"
                >
                  {requestingPayout ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Submit Request'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Creator Tier Card */}
        {tier && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-2xl border"
            style={{ 
              background: `linear-gradient(135deg, ${tier.color}15, ${tier.color}05)`,
              borderColor: `${tier.color}40`
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
                  style={{ background: `${tier.color}30` }}
                >
                  {tier.badge}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                    <span 
                      className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ background: `${tier.color}30`, color: tier.color }}
                    >
                      {tier.revenue_share}% Revenue Share
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm mt-1">
                    {tier.total_watch_hours} premium watch hours earned
                  </p>
                </div>
              </div>
              
              {tier.next_tier && (
                <div className="text-right">
                  <p className="text-zinc-500 text-sm">Next tier: {tier.next_tier.name} {tier.next_tier.badge}</p>
                  <p className="text-zinc-400 text-sm">
                    <span className="text-zinc-300 font-medium">{tier.next_tier.hours_needed}h</span> more to unlock {tier.next_tier.revenue_share}% share
                  </p>
                  <div className="mt-2 w-48 h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${Math.min(100, (tier.total_watch_hours / (tier.total_watch_hours + tier.next_tier.hours_needed)) * 100)}%`,
                        background: tier.color 
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Earnings Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-green-500/20 to-emerald-600/20 border border-green-500/30 rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-green-500/20">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-zinc-400 text-sm">Available Balance</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {formatCurrency(earnings?.available_balance)}
            </div>
            <p className="text-green-400 text-sm flex items-center gap-1">
              <ArrowUpRight className="w-4 h-4" />
              {formatCurrency(earnings?.estimated_earnings)} total earned
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-zinc-800/60">
                <Clock className="w-5 h-5 text-zinc-300" />
              </div>
              <span className="text-zinc-400 text-sm">Premium Watch Time</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {formatTime(earnings?.total_watch_time_hours || 0)}
            </div>
            <p className="text-zinc-500 text-sm">
              {earnings?.unique_premium_viewers || 0} premium viewers
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-white/10">
                <Video className="w-5 h-5 text-zinc-300" />
              </div>
              <span className="text-zinc-400 text-sm">Videos</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {analytics?.total_videos || 0}
            </div>
            <p className="text-zinc-500 text-sm">
              {earnings?.unique_videos_watched || 0} watched
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <Wallet className="w-5 h-5 text-orange-400" />
              </div>
              <span className="text-zinc-400 text-sm">Total Paid Out</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {formatCurrency(earnings?.total_paid_out)}
            </div>
            <p className="text-zinc-500 text-sm">
              {formatCurrency(earnings?.pending_payout)} pending
            </p>
          </motion.div>
        </div>

        {/* Tabs for different sections */}
        <Tabs defaultValue="videos" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="videos" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <BarChart3 className="w-4 h-4 mr-2" />
              Video Performance
            </TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <Wallet className="w-4 h-4 mr-2" />
              Payout History
            </TabsTrigger>
            <TabsTrigger value="tiers" className="data-[state=active]:bg-white data-[state=active]:text-black">
              <Trophy className="w-4 h-4 mr-2" />
              Tier Benefits
            </TabsTrigger>
          </TabsList>

          {/* Video Performance Tab */}
          <TabsContent value="videos">
            {analytics?.video_analytics?.length > 0 ? (
              <div className="space-y-3">
                {analytics.video_analytics.map((video, index) => (
                  <motion.div
                    key={video.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-white truncate">{video.title}</h4>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3.5 h-3.5" />
                            {video.views.toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3.5 h-3.5" />
                            {video.likes.toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatTime(video.premium_watch_hours)} premium
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            {video.unique_viewers} viewers
                          </span>
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-lg font-bold text-green-400">
                          {formatCurrency(video.earnings)}
                        </div>
                        <div className="text-xs text-zinc-500">earned</div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
                <Video className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-lg text-zinc-400 mb-2">No videos yet</h3>
                <p className="text-zinc-500">Upload your first AI video to start earning!</p>
              </div>
            )}
          </TabsContent>

          {/* Payout History Tab */}
          <TabsContent value="payouts">
            {payouts.length > 0 ? (
              <div className="space-y-3">
                {payouts.map((payout) => (
                  <div
                    key={payout.id}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{formatCurrency(payout.amount)}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          payout.status === 'completed' 
                            ? 'bg-green-500/20 text-green-400'
                            : payout.status === 'pending'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {payout.status === 'completed' && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                          {payout.status}
                        </span>
                      </div>
                      <div className="text-sm text-zinc-500 mt-1">
                        {payout.payout_method} • {new Date(payout.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
                <Wallet className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-lg text-zinc-400 mb-2">No payouts yet</h3>
                <p className="text-zinc-500">Request a payout when you reach ${earnings?.minimum_payout || 10} minimum</p>
              </div>
            )}
          </TabsContent>

          {/* Tier Benefits Tab */}
          <TabsContent value="tiers">
            <div className="grid gap-4">
              {fundInfo?.tiers?.map((tierInfo, index) => (
                <motion.div
                  key={tierInfo.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-zinc-900 border rounded-xl p-4 ${
                    tier?.name === tierInfo.name ? 'border-white/30 bg-white/5' : 'border-zinc-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-3xl">{tierInfo.badge}</div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-white">{tierInfo.name}</h4>
                          {tier?.name === tierInfo.name && (
                            <span className="px-2 py-0.5 bg-white/10 text-zinc-300 text-xs rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-zinc-500 text-sm">{tierInfo.requirement}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-green-400">{tierInfo.revenue_share}</div>
                      <div className="text-xs text-zinc-500">revenue share</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
